<script>
  import { onMount } from "svelte";
  import {
    Page,
    Card,
    CardHeader,
    CardContent,
    Block,
    List,
    ListItem,
    Button,
    Badge,
    f7,
  } from "framework7-svelte";
  import { ENGINE_URL } from "../services/service.config";
  import TradingNavbar from "../components/TradingNavbar.svelte";

  let activeDriver = "BSE_BOW";
  let environment = "SIMULATION";
  let autoFallback = true;
  let heartbeatStatus = "CHECKING...";
  let engineHealthy = false;
  let lastAckLatency = "-";
  let errorMessage = null;
  let isSaving = false;

  async function loadGatewaySettings() {
    errorMessage = null;
    try {
      // 1. Fetch live gateway config
      const cfgRes = await fetch(`${ENGINE_URL}/gateway/config`);
      if (cfgRes.ok) {
        const cfg = await cfgRes.json();
        if (cfg.active_driver) activeDriver = cfg.active_driver;
        if (cfg.environment) environment = cfg.environment;
      }

      // 2. Fetch live engine health & metrics
      const healthRes = await fetch(`${ENGINE_URL}/health`);
      if (healthRes.ok) {
        const health = await healthRes.json();
        engineHealthy = health.status === "HEALTHY" || health.status === "ok" || health.status === true;
        heartbeatStatus = engineHealthy ? "ONLINE (Active Socket)" : "DEGRADED";
      } else {
        engineHealthy = false;
        heartbeatStatus = "OFFLINE (HTTP Error)";
      }

      const metricsRes = await fetch(`${ENGINE_URL}/metrics`);
      if (metricsRes.ok) {
        const metrics = await metricsRes.json();
        if (metrics.last_ack_latency_ms !== undefined) {
          lastAckLatency = `${metrics.last_ack_latency_ms} ms`;
        } else if (metrics.uptime_seconds !== undefined) {
          lastAckLatency = `Uptime: ${metrics.uptime_seconds}s`;
        }
      }
    } catch (err) {
      console.error("Error loading gateway configuration from engine:", err);
      engineHealthy = false;
      heartbeatStatus = "OFFLINE / DISCONNECTED";
      errorMessage = err.message || "Failed to communicate with Go engine";
    }
  }

  onMount(() => {
    loadGatewaySettings();
  });

  async function saveSettings() {
    isSaving = true;
    try {
      const res = await fetch(`${ENGINE_URL}/gateway/config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          driver: activeDriver,
          environment: environment,
          auto_fallback: autoFallback,
        }),
      });
      if (!res.ok) {
        throw new Error(`Engine returned HTTP ${res.status}: ${res.statusText}`);
      }
      f7.toast.create({
        text: `Gateway settings successfully applied: Driver=${activeDriver}, Env=${environment}`,
        closeTimeout: 3000,
      }).open();
      await loadGatewaySettings();
    } catch (err) {
      console.error("Failed to apply gateway settings:", err);
      f7.toast.create({
        text: `Failed to save gateway config: ${err.message}`,
        closeTimeout: 4000,
      }).open();
    } finally {
      isSaving = false;
    }
  }
</script>

<Page name="gateway-settings">
  <TradingNavbar title="Exchange Gateway & Driver Settings" />

  <Block>
    {#if errorMessage}
      <Card class="bg-color-red text-color-white margin-bottom">
        <CardContent>
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div>
              <strong>⚠️ Engine Connection Error</strong>
              <div style="font-size: 13px; margin-top: 4px;">{errorMessage}</div>
            </div>
            <Button fill small color="white" text-color="red" onClick={loadGatewaySettings}>
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    {/if}

    <Card class="bg-color-teal text-color-white margin-bottom">
      <CardContent>
        <div style="display: flex; align-items: center; gap: 10px;">
          <Badge color="white" text-color="teal" style="font-weight:bold;">COMPLIANCE GUARANTEE</Badge>
          <div style="font-size: 13px;">
            <strong>BSE BOW Conformance Suite Always Ready:</strong> The dedicated BSE BOW V1.0.7 Conformance & Wire Inspection tab operates independently against BSE Member Simulation at all times, regardless of which driver is active here.
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>Select Execution Driver</CardHeader>
      <CardContent padding={false}>
        <List>
          <ListItem
            radio
            name="execution-driver"
            value="BSE_BOW"
            title="BSE BOLT+ on Web (BOW) Direct Socket"
            footer="Native TCP persistent socket (port 8083) with microsecond execution latency."
            checked={activeDriver === "BSE_BOW"}
            onChange={() => (activeDriver = "BSE_BOW")}
          >
            {#if activeDriver === "BSE_BOW"}
              <Badge slot="after" color="green">SELECTED</Badge>
            {/if}
          </ListItem>

          <ListItem
            radio
            name="execution-driver"
            value="NSCRIPT"
            title="NScript Broker Gateway (HTTP API)"
            footer="HTTP POST JSON/REST order execution for NScript broker line."
            checked={activeDriver === "NSCRIPT"}
            onChange={() => (activeDriver = "NSCRIPT")}
          >
            {#if activeDriver === "NSCRIPT"}
              <Badge slot="after" color="blue">SELECTED</Badge>
            {/if}
          </ListItem>
        </List>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>Select Operating Environment</CardHeader>
      <CardContent padding={false}>
        <List>
          <ListItem
            radio
            name="operating-env"
            value="PRODUCTION"
            title="Production (Live Capital Line)"
            footer="Direct execution with real client margin checks and real exchange fills."
            checked={environment === "PRODUCTION"}
            onChange={() => (environment = "PRODUCTION")}
          >
            {#if environment === "PRODUCTION"}
              <Badge slot="after" color="red">LIVE PROD</Badge>
            {/if}
          </ListItem>

          <ListItem
            radio
            name="operating-env"
            value="SIMULATION"
            title="Simulation / Dev (Paper Sandbox)"
            footer="Virtual fills, synthetic ticks, and zero exchange financial risk."
            checked={environment === "SIMULATION"}
            onChange={() => (environment = "SIMULATION")}
          >
            {#if environment === "SIMULATION"}
              <Badge slot="after" color="orange">DEV / SIM</Badge>
            {/if}
          </ListItem>
        </List>
      </CardContent>
    </Card>

    <Block class="margin-top">
      <Button
        fill
        large
        color="blue"
        preloader
        loading={isSaving}
        onClick={saveSettings}
      >
        Apply & Switch Gateway Settings
      </Button>
    </Block>

    <Card>
      <CardHeader>Active Connection Telemetry & Health</CardHeader>
      <CardContent padding={false}>
        <List>
          <ListItem title="Active Execution Driver" after={activeDriver} />
          <ListItem title="Active Environment" after={environment} />
          <ListItem title="Engine Socket Status" after={heartbeatStatus} />
          <ListItem title="Round-Trip ACK Latency / Uptime" after={lastAckLatency} />
        </List>
      </CardContent>
    </Card>
  </Block>
</Page>
